import {
  Button,
  Divider,
  HStack,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminEditDistributionGroupMutation,
  InternalAdminEditDistributionGroupMutationVariables,
  InternalAdminUserFragment,
} from "gql/graphql";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import {
  InboxIcon,
  InboxSlashIcon,
  GroupsOutlineIcon,
  AdminAddToGroupIcon,
} from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { useMutation } from "@apollo/client";
import {
  getDistributionGroupMention,
  sortByName,
} from "modules/utils/Functions";
import useInternalAdminDistributionGroupList from "../hooks/use-internal-admin-distribution-group-list";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-edit-distribution-group";

interface IProps {
  user: InternalAdminUserFragment;
}

const InternalAdminUserDistributionGroups = ({ user }: IProps) => {
  const { openModal } = useOverlay();
  const { distributionGroups } = useInternalAdminDistributionGroupList({
    organizationId: user.organizationId,
    input: {
      limit: 100000,
      offset: 0,
    },
  });

  const distributionGroupList = useMemo(
    () =>
      distributionGroups
        .filter((obj) => obj.userIdList.includes(user.id))
        .sort(sortByName),
    [user.id, distributionGroups],
  );

  const [internalAdminEditDistributionGroupMutationAPI] = useMutation<
    InternalAdminEditDistributionGroupMutation,
    InternalAdminEditDistributionGroupMutationVariables
  >(INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const onToggleNotificationsDistributionGroupClicked = useCallback(
    (distributionGroupId: string, enableNotifications: boolean) => () => {
      if (enableNotifications) {
        internalAdminEditDistributionGroupMutationAPI({
          variables: {
            organizationId: user.organizationId,
            id: distributionGroupId,
            input: {
              notificationUserIdListToAdd: [user.id],
            },
          },
        });
        return;
      }
      internalAdminEditDistributionGroupMutationAPI({
        variables: {
          organizationId: user.organizationId,
          id: distributionGroupId,
          input: {
            notificationUserIdListToRemove: [user.id],
          },
        },
      });
    },
    [
      internalAdminEditDistributionGroupMutationAPI,
      user.id,
      user.organizationId,
    ],
  );

  const onAddToGroupsClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_ADD_USERS_TO_DISTRIBUTION_GROUPS,
      ids: [user.id],
    });
  }, [openModal, user.id]);

  return (
    <VStack w="full" alignItems="flex-start">
      <HStack w="full">
        <GroupsOutlineIcon boxSize={6} fill={colors.blackSecondary} />
        <Text fontWeight={700} fontSize={18}>
          Groups ({distributionGroupList.length})
        </Text>
        <Spacer />
        <Button
          variant="hover"
          fontSize={14}
          fontWeight={600}
          size="xs"
          color={colors.orangePrimary}
          leftIcon={
            <AdminAddToGroupIcon boxSize={4} fill={colors.orangePrimary} />
          }
          onClick={onAddToGroupsClicked}
        >
          Add to Group(s)
        </Button>
      </HStack>
      <Divider borderColor={colors.black} />
      {distributionGroupList.length === 0 && (
        <Text color={colors.gray50} fontStyle="italic" fontSize={14}>
          No groups assigned yet
        </Text>
      )}
      <VStack w="full" divider={<Divider />}>
        {distributionGroupList.map((distributionGroup) => {
          const notifcationsEnabled =
            distributionGroup.notificationUserIdList.includes(user.id);
          return (
            <HStack key={distributionGroup.id} w="full">
              <Text
                color={
                  distributionGroup.active ? colors.blue : colors.grayPrimary
                }
                fontSize={14}
                fontWeight={700}
              >
                {getDistributionGroupMention(distributionGroup)}
              </Text>
              <Spacer />
              <CircularIconButton
                aria-label={
                  notifcationsEnabled ? "notifications on" : "notifications off"
                }
                variant="ghost"
                onClick={onToggleNotificationsDistributionGroupClicked(
                  distributionGroup.id,
                  !notifcationsEnabled,
                )}
                icon={
                  notifcationsEnabled ? (
                    <SyncoTooltip label="Group messages sent to Threads Inbox">
                      <InboxIcon fill={colors.blue} boxSize={5} />
                    </SyncoTooltip>
                  ) : (
                    <SyncoTooltip label="Group messages not sent to Threads Inbox">
                      <InboxSlashIcon fill={colors.grayMain} boxSize={5} />
                    </SyncoTooltip>
                  )
                }
              />
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

export default memo(InternalAdminUserDistributionGroups);
